import type { PlanGroup, PriorityGroup, RawPlan, TreatmentProcedure } from 'app/types/treatment'
import { isDeadPlan } from 'lib/treatmentCards'

const NO_PRIORITY = `No priority`

/**
 * Only whitespace is forgiven. Kolla sends the label exactly as the practice
 * typed it, and `DECLINED` or `URG-1` are meaning, not noise.
 */
const clean = (priority: string | null | undefined): string => (priority ?? ``).trim()

const asNumber = (priority: string): number | undefined => {
  const value = Number(priority)
  return priority !== `` && Number.isFinite(value) ? value : undefined
}

/**
 * How a priority reads on screen: "Priority 7" for a number, the practice's
 * own word verbatim otherwise, and nothing at all for an unranked procedure.
 */
export function priorityLabel(priority: string | null | undefined): string | undefined {
  const value = clean(priority)
  if (value === ``) return undefined
  const number = asNumber(value)
  return number === undefined ? value : `Priority ${number}`
}

/**
 * Procedures piled by the practice's own priority.
 *
 * Open Dental hands out priorities as a text label per practice. Most
 * practices number them, and some also write words. So: every label that
 * reads as a number comes first, in numeric order (1, 2, 10 rather than
 * 1, 10, 2), then every other label alphabetically and verbatim, then one
 * last pile for the procedures nobody ranked. That last pile is most of a
 * plan at many practices, and hiding it would hide most of the money.
 *
 * Rows keep the order they arrived in, which is fee-first off `buildCard`.
 */
export function priorityGroups(procedures: TreatmentProcedure[]): PriorityGroup[] {
  // Keyed on the number where there is one, so "07", "7" and "7.0" are one
  // group with one heading rather than three headings that all read the same.
  const byKey = new Map<string, TreatmentProcedure[]>()
  for (const procedure of procedures) {
    const raw = clean(procedure.priority)
    const number = asNumber(raw)
    const key = number === undefined ? raw : String(number)
    const rows = byKey.get(key) ?? []
    rows.push(procedure)
    byKey.set(key, rows)
  }

  const keys = [...byKey.keys()].filter((key) => key !== ``)
  const numbered = keys
    .filter((key) => asNumber(key) !== undefined)
    .sort((a, b) => (asNumber(a) as number) - (asNumber(b) as number))
  const worded = keys.filter((key) => asNumber(key) === undefined).sort((a, b) => a.localeCompare(b))

  const groups = [...numbered, ...worded].map((key) => ({
    label: priorityLabel(key) as string,
    rows: byKey.get(key) as TreatmentProcedure[],
  }))
  const unranked = byKey.get(``)
  if (unranked) groups.push({ label: NO_PRIORITY, rows: unranked })
  return groups
}

/** Whether a priority grouping would say anything: one ranked procedure is enough. */
export function anyPrioritized(procedures: TreatmentProcedure[]): boolean {
  return procedures.some((procedure) => clean(procedure.priority) !== ``)
}

export const NOT_ON_A_PLAN = `Not on a plan`

/**
 * Procedures piled by the plan that carries them, as the practice system has
 * the plans.
 *
 * Revisions of a plan point at the same live work, so a procedure shows under
 * every plan it is on, never de-duplicated. Live plans come first, newest
 * first; closed plans follow. Work whose plan ids resolve to no plan we hold
 * gets its own pile before the work no plan carries at all.
 */
export function planGroups(procedures: TreatmentProcedure[], plans: RawPlan[]): PlanGroup[] {
  const groups: PlanGroup[] = plans
    .map((plan) => ({
      id: plan.id,
      label: plan.displayName?.trim() || `Untitled plan`,
      created: plan.planCreateDate?.slice(0, 10) || null,
      dead: isDeadPlan(plan),
      rows: procedures.filter((procedure) => procedure.planIds?.includes(plan.id)),
    }))
    .filter((group) => group.rows.length > 0)
    .sort((a, b) => Number(a.dead) - Number(b.dead) || (b.created ?? ``).localeCompare(a.created ?? ``))

  const placed = new Set(groups.flatMap((group) => group.rows))
  const unresolved = procedures.filter((procedure) => !procedure.offPlan && !placed.has(procedure))
  const offPlan = procedures.filter((procedure) => procedure.offPlan)
  if (unresolved.length > 0) {
    groups.push({ id: `unresolved`, label: `Plan not on record`, created: null, dead: false, rows: unresolved })
  }
  if (offPlan.length > 0)
    groups.push({ id: `off-plan`, label: NOT_ON_A_PLAN, created: null, dead: false, rows: offPlan })
  return groups
}

/** The user setting the case dialog keeps its grouping under. */
export const GROUPING_SETTING = `treatmentCaseGrouping`

/** The contact drawer's Treatment Plans tab remembers its own choice: it offers Plan, the case dialog does not. */
export const DRAWER_GROUPING_SETTING = `treatmentDrawerGrouping`
